import React, { useState } from "react";
import { Box, Typography, Button, CircularProgress } from "@mui/material";
import apiService from "../../services/api";
import { FILE_UPLOAD_CONFIG } from "../../constants";

type BaseFileUploadInputProps = {
  field: string;
  formData: any;
  label?: string;
  required?: boolean;
  error?: string;
  folderPath?: string;
  folderType?: string;
  onImageUpload?: (field: string, file: File) => Promise<void>;
  uploading?: boolean;
  hideLabel?: boolean;
  accept?: string;
};

type FileUploadInputProps = BaseFileUploadInputProps &
  (
    | {
        multiple?: false;
        maxFiles?: 1;
        onChange: (field: string, value: string) => void;
      }
    | {
        multiple: true;
        maxFiles?: number;
        onChange: (field: string, value: string[]) => void;
      }
  );

const FileUploadInput: React.FC<FileUploadInputProps> = (props) => {
  const {
    field,
    label,
    required = false,
    error,
    folderPath = 'staff',
    folderType = 'profile',
    onImageUpload,
    uploading: externalUploading = false,
    hideLabel = false,
    accept = 'image/*',
  } = props;
  
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  
  const isUploading = externalUploading || uploading;

  const dataUrlToBlob = async (dataUrl: string): Promise<Blob> => {
    const res = await fetch(dataUrl);
    return await res.blob();
  };

  const compressImageToTarget = async (file: File): Promise<File> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = async () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) return reject(new Error('Canvas not supported'));

        let { width, height } = img as HTMLImageElement;
        const maxDim = FILE_UPLOAD_CONFIG.IMAGE_MAX_DIMENSION;
        if (width > height && width > maxDim) {
          height = Math.round((height * maxDim) / width);
          width = maxDim;
        } else if (height >= width && height > maxDim) {
          width = Math.round((width * maxDim) / height);
          height = maxDim;
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        let quality = 0.92;
        let step = FILE_UPLOAD_CONFIG.IMAGE_QUALITY_STEP;
        let dataUrl = canvas.toDataURL('image/jpeg', quality);
        let blob = await dataUrlToBlob(dataUrl);
        while (blob.size > FILE_UPLOAD_CONFIG.IMAGE_TARGET_MAX_BYTES && quality > 0.3) {
          quality = Math.max(0.3, quality - step);
          dataUrl = canvas.toDataURL('image/jpeg', quality);
          blob = await dataUrlToBlob(dataUrl);
        }

        const compressedFile = new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' });
        resolve(compressedFile);
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      const reader = new FileReader();
      reader.onload = () => {
        img.src = String(reader.result || '');
      };
      reader.onerror = () => reject(new Error('Failed to read image'));
      reader.readAsDataURL(file);
    });
  };

  const uploadSingleFile = async (file: File): Promise<string> => {
    const isImage = FILE_UPLOAD_CONFIG.ALLOWED_IMAGE_TYPES.includes(file.type);
    const isVideo = file.type === 'video/mp4' || file.name.toLowerCase().endsWith('.mp4');
    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    
    if (!isImage && !isVideo && !isPdf) {
      throw new Error('Please select a valid image');
    }

    let uploadFile = file;
    
    if (isImage) {
      try {
        uploadFile = await compressImageToTarget(file);
      } catch (e) {
        console.warn("Could not compress image, uploading original.", e);
      }
    }
    // Videos are uploaded as-is without compression

    let actualFolderPath = folderPath;
    let actualFolderType = folderType;
    
    if (folderPath === 'settings' && folderType === 'document' && isPdf) {
      actualFolderPath = 'regulatory';
      actualFolderType = 'regulatory';
    }

    const response = await apiService.uploadImage(uploadFile, actualFolderPath, actualFolderType);

    let fileUrl = file.name;
    if (response?.data?.savedFiles && response.data.savedFiles.length > 0) {
      const savedFile = response.data.savedFiles[0];
      fileUrl = savedFile.filePath || savedFile.fileName || file.name;
    } else if (response?.data) {
      fileUrl = response.data.url || response.data.filePath || response.data.fileName || response.data.imageUrl || file.name;
    }
    return fileUrl;
  };

  const handleFilesSelected = async (files: FileList) => {
    const filesToUpload = Array.from(files);

    if (props.multiple && filesToUpload.length > (props.maxFiles || 1)) {
      setUploadError(`You can only upload a maximum of ${props.maxFiles} files.`);
      return;
    }

    setUploading(true);
    setUploadError(null);
    setUploadSuccess(false);

    try {
      if (props.multiple) {
        if (onImageUpload) {
          const uploadPromises = filesToUpload.map(file => onImageUpload(field, file));
          await Promise.all(uploadPromises);
        } else {
          const uploadPromises = filesToUpload.map(f => uploadSingleFile(f));
          const urls = await Promise.all(uploadPromises);
          const validUrls = urls.filter(Boolean);
          if (validUrls.length > 0) {
            props.onChange(field, validUrls);
          }
          if (validUrls.length < filesToUpload.length) {
            throw new Error('Some files failed to upload.');
          }
        }
      } else if (filesToUpload.length > 0) {
        if (onImageUpload) {
          await onImageUpload(field, filesToUpload[0]);
        } else {
          const url = await uploadSingleFile(filesToUpload[0]);
          (props.onChange as (field: string, value: string) => void)(field, url);
        }
      }
      setUploadSuccess(true);
    } catch (error: any) {
      console.error('File upload error:', error);
      setUploadError(error.message || 'Failed to upload file(s). Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
  <Box>
    {label && !hideLabel && (
      <Typography variant="body2" className="form-label fw-medium mb-2">
        {label} {required && <span style={{ color: "red" }}>*</span>}
      </Typography>
    )}
    <Button
      component="label"
      variant="outlined"
      disabled={isUploading}
      sx={{
        width: hideLabel ? "auto" : "100%",
        height: hideLabel ? "32px" : "43px",
        minWidth: hideLabel ? "32px" : "auto",
        border: error || uploadError ? "1px solid #d32f2f" : hideLabel ? "none" : "1px solid #d0d0d0",
        borderRadius: hideLabel ? "4px" : "6px",
        backgroundColor: isUploading ? "#f5f5f5" : hideLabel ? "transparent" : "#ffffff",
        color: isUploading ? "#999999" : (error || uploadError) ? "#d32f2f" : (hideLabel ? "#666666" : "#666666"),
        textTransform: "none",
        fontSize: hideLabel ? "16px" : "14px",
        fontWeight: 400,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: hideLabel ? 0 : 1,
        p: hideLabel ? 0.5 : 1,
        "&:hover": {
          backgroundColor: isUploading ? "#f5f5f5" : hideLabel ? "rgba(0,0,0,0.04)" : "#f5f5f5",
          borderColor: error || uploadError ? "#d32f2f" : hideLabel ? "transparent" : "#999999",
        },
        "&:focus": {
          borderColor: error || uploadError ? "#d32f2f" : hideLabel ? "transparent" : "#1976d2",
          boxShadow: error || uploadError ? "0 0 0 2px rgba(211, 47, 47, 0.2)" : hideLabel ? "none" : "0 0 0 2px rgba(25, 118, 210, 0.2)",
        },
        "&:disabled": {
          backgroundColor: "#f5f5f5",
          color: "#999999",
          borderColor: "#d0d0d0",
        },
      }}
    >
      {/* Upload Icon */}
      <svg
        width={hideLabel ? "20" : "25"}
        height={hideLabel ? "20" : "25"}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ color: (error || uploadError) ? "#d32f2f" : "#666666" }}
      >
        <path
          d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
        <path
          d="M14 2V8H20"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M12 18V12"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M9 15L12 12L15 15"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      {isUploading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={16} color="inherit" />
          Uploading...
        </Box>
      ) : (
        <Box sx={{ color: (error || uploadError) ? "#d32f2f" : "inherit" }}>
          {(() => {
            switch (field) {
              case 'frontImage':
                return 'Upload Front Image';
              case 'backImage':
                return 'Upload Back Image';
              case 'vehicleimages':
                return 'Upload Vehicle Images';
              case 'battery1images':
                return 'Upload Battery No 1';
              case 'battery2images':
                return 'Upload Battery No 2';
              case 'medias':
                return hideLabel ? '📷' : 'Upload Media Files';
              default:
                return 'Upload Photo';
            }
          })()}
        </Box>
      )}
      <input
        type="file"
        hidden
        multiple={props.multiple}
        accept={accept}
        onChange={(e) => {
          const files = e.target.files;
          if (files && files.length > 0) {
            handleFilesSelected(files);
          }
          // Reset the input value to allow re-uploading the same file
          e.target.value = '';
        }}
      />
    </Button>
  
    {uploadError && (
      <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
        {uploadError}
      </Typography>
    )}
    {error && (
      <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
        {error}
      </Typography>
    )}
  </Box>
  );
};

export default FileUploadInput;
